import { useCallback, useEffect, useRef } from 'react';
import {
  createFilterOptions,
  FormControl,
  Grid,
  TextField,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CameraTimeLapseFrameDetails } from 'features/cameras/cameras.types';
import { useQueryParams } from 'hooks/useQueryParams';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { AutocompleteWithBackdrop } from 'components/AutocompleteWithBackdrop';
import { CameraOption, useAlertCameraOptions } from './useAlertCameraOptions';
import { CameraTimelapse } from './CameraTimelapse';
import { MAX_CAMERA_OPTIONS } from './MediaForm.constants';

// Options are sorted viewport-first then most-recent, so the capped set is the
// most relevant one. Typing still matches against all cameras before the cap.
const filterCameraOptions = createFilterOptions<CameraOption>({
  limit: MAX_CAMERA_OPTIONS,
});

export const MediaAlertCameraForm = (): JSX.Element => {
  const { t } = useTranslation();
  const { control, formState, watch, setValue } = useFormContext();
  const { cameraOptions, isLoading } = useAlertCameraOptions();
  const isCameraLimitEnabled = useReleaseFlag(
    'limit_alert_cameras_in_frontend',
  );
  const [{ alertCameraId, alertCameraTs }] = useQueryParams<{
    alertCameraId?: string;
    alertCameraTs?: string;
  }>();

  const selectedCamera = watch('media');
  const initializedAlertCameraIdRef = useRef<string | null>(null);
  const isCameraInOptions = cameraOptions.some(
    (camera) => camera.id === selectedCamera?.id,
  );

  useEffect(() => {
    if (!alertCameraId) return;
    if (initializedAlertCameraIdRef.current === alertCameraId) return;
    if (selectedCamera?.id) return;

    const initialSelectedCamera = cameraOptions.find(
      (camera) => camera.id === alertCameraId,
    );
    if (!initialSelectedCamera) return;

    setValue('media', initialSelectedCamera, { shouldDirty: false });
    initializedAlertCameraIdRef.current = alertCameraId;
  }, [setValue, cameraOptions, alertCameraId, selectedCamera?.id]);

  useEffect(() => {
    if (selectedCamera?.id && !isCameraInOptions) {
      // Don't mark dirty - this is automatic cleanup when camera becomes unavailable
      setValue('media', null, { shouldDirty: false });
    }
  }, [isCameraInOptions, selectedCamera?.id, setValue]);

  const handleFrameSelect = useCallback(
    (frame?: CameraTimeLapseFrameDetails): void => {
      if (!frame) return;
      setValue(
        'media',
        {
          ...selectedCamera,
          ...frame,
          imageTimestamp: new Date(frame.imageTimestamp),
        },
        { shouldDirty: true },
      );
    },
    [selectedCamera, setValue],
  );

  return (
    <Grid container spacing={2} sx={{ width: '100%' }}>
      <Grid size={12} sx={{ position: 'relative' }}>
        <Controller
          name="media"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            return (
              <FormControl fullWidth>
                <AutocompleteWithBackdrop<CameraOption>
                  id="media"
                  options={cameraOptions}
                  filterOptions={
                    isCameraLimitEnabled ? filterCameraOptions : undefined
                  }
                  getOptionDisabled={(option) => option.isOffline}
                  getOptionLabel={(option): string => option.name || ''}
                  isOptionEqualToValue={(option, value): boolean =>
                    option.id === value?.id
                  }
                  value={field.value}
                  onChange={(_, value): void => field.onChange(value)}
                  onBlur={field.onBlur}
                  renderInput={(params): JSX.Element => (
                    <TextField
                      {...params}
                      name={field.name}
                      inputRef={field.ref}
                      variant="outlined"
                      label={t('addIncidentReport.inputs.alertCamera.label')}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      disabled={formState.isSubmitting || isLoading}
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          'aria-label': t(
                            'addIncidentReport.inputs.alertCamera.ariaLabel',
                          ),
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            );
          }}
        />
      </Grid>

      {selectedCamera && (
        <Grid size={{ xs: 12, md: 6 }}>
          <CameraTimelapse
            camera={selectedCamera}
            onFrameSelect={handleFrameSelect}
            initialFrameTs={alertCameraTs}
          />
        </Grid>
      )}
    </Grid>
  );
};
